import { showOperationError } from "../lib/notifications";
import { toast } from "sonner";
import {
  FollowupSettings,
  CrmUpdateSettings,
  OutcomeSettings,
  EscalationSettings,
  escalationStageError,
} from "@ai-crm/agent-ui";

import {
  emptyFunnelPromptFields,
  editablePromptFields,
  type FunnelPromptFields,
} from "@ai-crm/shared";
import { AdminPromptEditor } from "../components/funnel-prompt-editor";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "../components/confirm-dialog";
import { PageHeader } from "../components/layout";
import type { AdminFunnel, LlmModel } from "../lib/api";
import { ApiError, adminApi } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { DialogsTab } from "./funnel/dialogs-tab";
import { RunsTab } from "./funnel/runs-tab";

const models: Array<{ value: LlmModel; label: string }> = [
  { value: "GPT4O_MINI", label: "GPT-4o mini" },
  { value: "GPT4O", label: "GPT-4o" },
  { value: "CLAUDE_HAIKU", label: "Claude Haiku" },
  { value: "CLAUDE_SONNET", label: "Claude Sonnet" },
];

const conflictMessage =
  "Данные изменены другим администратором. Перезагрузите актуальную версию.";

type FunnelTab = "settings" | "channels" | "test" | "runs" | "dialogs";

interface TestTurn {
  id: number;
  role: "USER" | "ASSISTANT";
  content: string;
  proposedActions?: import("@ai-crm/shared").ProposedAgentAction[];
}

function isLlmModel(value: string): value is LlmModel {
  return models.some((model) => model.value === value);
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409 && error.code === "CONFLICT") return conflictMessage;
    if (error.code === "INVALID_AGENT_DECISION")
      return "Ответ модели не прошёл проверку формата. Код: INVALID_AGENT_DECISION.";
    if (error.code === "LLM_RESPONSE_REJECTED")
      return "Модель не вернула завершённый ответ. Код: LLM_RESPONSE_REJECTED.";
  }
  return error instanceof Error
    ? error.message
    : "Не удалось выполнить действие";
}

function FunnelTabs({
  active,
  funnelId,
}: {
  active: FunnelTab;
  funnelId: string;
}) {
  const tabs: Array<{ id: FunnelTab; label: string }> = [
    { id: "settings", label: "Настройки" },
    { id: "channels", label: "Каналы" },
    { id: "test", label: "Тест" },
    { id: "runs", label: "Запуски" },
    { id: "dialogs", label: "Диалоги" },
  ];
  return (
    <nav
      className="mb-4 flex gap-1 overflow-x-auto border-b"
      aria-label="Разделы агента"
    >
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        const href =
          tab.id === "settings"
            ? `/funnels/${funnelId}`
            : `/funnels/${funnelId}/${tab.id}`;
        return (
          <Link
            key={tab.id}
            className={`shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              isActive
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            to={href}
            aria-current={isActive ? "page" : undefined}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

function isFunnelRouteTab(
  value: string | undefined,
): value is Exclude<FunnelTab, "settings"> {
  return (
    value === "channels" ||
    value === "test" ||
    value === "runs" ||
    value === "dialogs"
  );
}

export function FunnelPage() {
  const { id, tab } = useParams();
  const { token } = useAdminAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeTab: FunnelTab | null =
    tab === undefined ? "settings" : isFunnelRouteTab(tab) ? tab : null;
  const [name, setName] = useState("");
  const [persistedName, setPersistedName] = useState("");
  const [statusIds, setStatusIds] = useState<string[]>([]);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [promptFields, setPromptFields] = useState<FunnelPromptFields>(
    emptyFunnelPromptFields,
  );
  const [model, setModel] = useState<LlmModel>("GPT4O_MINI");
  const [stopPhrase, setStopPhrase] = useState("");
  const [escalateOnGoal, setEscalateOnGoal] = useState(false);
  const [goalStatusId, setGoalStatusId] = useState<string | null>(null);
  const [allowLeadUpdates, setAllowLeadUpdates] = useState(false);
  const [allowContactUpdates, setAllowContactUpdates] = useState(false);
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [escalationStatusId, setEscalationStatusId] = useState<string | null>(
    null,
  );
  const [escalate, setEscalate] = useState(false);
  const [active, setActive] = useState(true);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState("");
  const [settingsPending, setSettingsPending] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState("");
  const [channelPending, setChannelPending] = useState(false);
  const [channelError, setChannelError] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState("");
  const [testTurns, setTestTurns] = useState<TestTurn[]>([]);
  const [testPending, setTestPending] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConflict, setDeleteConflict] = useState(false);
  const initializedFunnelIdRef = useRef<string | null>(null);
  const nextTurnIdRef = useRef(1);

  const funnel = useQuery({
    queryKey: ["admin", "funnel", id],
    queryFn: () => adminApi.funnel(token!, id!),
    enabled: Boolean(token && id),
  });
  const pipelines = useQuery({
    queryKey: ["admin", "account", funnel.data?.accountId, "amo", "pipelines"],
    queryFn: () => adminApi.amoPipelines(token!, funnel.data!.accountId),
    enabled: Boolean(token && funnel.data && activeTab === "settings"),
  });
  const statuses = useQuery({
    queryKey: [
      "admin",
      "account",
      funnel.data?.accountId,
      "amo",
      "pipelines",
      funnel.data?.amoPipelineId,
      "statuses",
    ],
    queryFn: () =>
      adminApi.amoPipelineStatuses(
        token!,
        funnel.data!.accountId,
        funnel.data!.amoPipelineId,
      ),
    enabled: Boolean(token && funnel.data && activeTab === "settings"),
  });
  const wazzupChannels = useQuery({
    queryKey: [
      "admin",
      "account",
      funnel.data?.accountId,
      "wazzup",
      "channels",
    ],
    queryFn: () => adminApi.wazzupChannels(token!, funnel.data!.accountId),
    enabled: Boolean(token && funnel.data && activeTab === "channels"),
  });
  useEffect(() => {
    const data = funnel.data;
    if (!data || initializedFunnelIdRef.current === data.id) return;
    initializedFunnelIdRef.current = data.id;
    setName(data.name);
    setPersistedName(data.name);
    setStatusIds(data.amoStatusIds);
    setSystemPrompt(data.systemPrompt);
    setPromptFields({
      ...emptyFunnelPromptFields(),
      ...editablePromptFields({ ...emptyFunnelPromptFields(), ...data }),
      promptVersion: data.promptVersion ?? 1,
    });
    setModel(data.model);
    setStopPhrase(data.stopPhrase ?? "");
    setEscalate(data.escalate);
    setEscalateOnGoal(data.escalateOnGoal ?? false);
    setGoalStatusId(data.goalStatusId ?? null);
    setAllowLeadUpdates(data.allowLeadUpdates ?? false);
    setAllowContactUpdates(data.allowContactUpdates ?? false);
    setEscalationEnabled(data.escalationEnabled ?? false);
    setEscalationStatusId(data.escalationStatusId ?? null);
    setActive(data.active);
    setExpectedUpdatedAt(data.updatedAt);
    setSettingsError(null);
    setChannelError(null);
    setDeleteError(null);
    setDeleteConflict(false);
  }, [funnel.data]);

  if (!id) return <Navigate to="/accounts" replace />;
  if (activeTab === null) {
    return <Navigate to={`/funnels/${id}`} replace />;
  }
  if (funnel.isLoading) {
    return (
      <div className="panel p-4 text-sm text-muted-foreground">
        Загружаем агента...
      </div>
    );
  }
  if (funnel.error || !funnel.data) {
    return (
      <div className="panel p-4 text-sm text-danger">
        {funnel.error?.message ?? "Агент не найден"}
      </div>
    );
  }

  const funnelData = funnel.data;
  const liveStatusIds = new Set(
    (statuses.data ?? []).map((status) => status.id),
  );
  const missingStatusIds = statuses.isSuccess
    ? statusIds.filter((statusId) => !liveStatusIds.has(statusId))
    : [];

  async function invalidateFunnelData(accountId: string, funnelId: string) {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["admin", "funnel", funnelId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", accountId, "funnels"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", accountId],
      }),
      queryClient.invalidateQueries({ queryKey: ["admin", "accounts"] }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", accountId, "audit"],
      }),
    ]);
  }

  function toggleStatus(statusId: string) {
    setStatusIds((current) =>
      current.includes(statusId)
        ? current.filter((item) => item !== statusId)
        : [...current, statusId],
    );
  }

  async function handleSettingsSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const goalError = escalationStageError(
      escalateOnGoal,
      goalStatusId,
      statusIds,
    );
    if (goalError) {
      setSettingsError(goalError);
      return;
    }
    const stageError = escalationStageError(
      escalationEnabled,
      escalationStatusId,
      statusIds,
    );
    if (stageError) {
      setSettingsError(stageError);
      return;
    }
    if (settingsPending) return;
    if (!name.trim()) {
      setSettingsError("Укажите название агента");
      return;
    }
    if (!systemPrompt.trim()) {
      setSettingsError("Укажите системный промпт");
      return;
    }
    setSettingsPending(true);
    setSettingsError(null);
    try {
      const updated = await adminApi.updateFunnel(token!, funnelData.id, {
        expectedUpdatedAt,
        name: name.trim(),
        amoStatusIds: statusIds,
        systemPrompt,
        ...editablePromptFields(promptFields),
        model,
        stopPhrase: stopPhrase.trim() || null,
        escalate,
        escalateOnGoal,
        goalStatusId,
        expectedCapabilitiesVersion: funnel.data?.capabilitiesVersion,
        allowLeadUpdates,
        allowContactUpdates,
        escalationEnabled,
        escalationStatusId,
        active,
      });
      setName(updated.name);
      setPersistedName(updated.name);
      setStatusIds(updated.amoStatusIds);
      setSystemPrompt(updated.systemPrompt);
      setPromptFields({
        ...emptyFunnelPromptFields(),
        ...editablePromptFields({ ...emptyFunnelPromptFields(), ...updated }),
        promptVersion: updated.promptVersion ?? 1,
      });
      setModel(updated.model);
      setStopPhrase(updated.stopPhrase ?? "");
      setEscalate(updated.escalate);
      setEscalateOnGoal(updated.escalateOnGoal ?? false);
      setGoalStatusId(updated.goalStatusId ?? null);
      setAllowLeadUpdates(updated.allowLeadUpdates ?? false);
      setAllowContactUpdates(updated.allowContactUpdates ?? false);
      setEscalationEnabled(updated.escalationEnabled ?? false);
      setEscalationStatusId(updated.escalationStatusId ?? null);
      setActive(updated.active);
      setExpectedUpdatedAt(updated.updatedAt);
      toast.success("Настройки сохранены.");
      queryClient.setQueryData<AdminFunnel>(
        ["admin", "funnel", funnelData.id],
        (current) => (current ? { ...current, ...updated } : current),
      );
      await invalidateFunnelData(funnelData.accountId, funnelData.id);
    } catch (error) {
      showOperationError(error, errorMessage(error), setSettingsError);
    } finally {
      setSettingsPending(false);
    }
  }

  async function invalidateChannelData() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["admin", "funnel", funnelData.id],
      }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", funnelData.accountId, "funnels"],
      }),
      queryClient.invalidateQueries({
        queryKey: [
          "admin",
          "account",
          funnelData.accountId,
          "wazzup",
          "channels",
        ],
      }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", funnelData.accountId, "audit"],
      }),
    ]);
  }

  async function handleBindChannel(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (channelPending || !selectedChannelId) return;
    const selected = wazzupChannels.data?.find(
      (channel) => channel.id === selectedChannelId,
    );
    const alreadyConnected = funnelData.channels.some(
      (channel) => channel.wazzupChannelId === selectedChannelId,
    );
    if (!selected || alreadyConnected) {
      setChannelError(
        "Выберите ещё не подключённый к этому агенту канал Wazzup",
      );
      return;
    }
    setChannelPending(true);
    setChannelError(null);
    try {
      await adminApi.bindChannel(token!, funnelData.id, {
        wazzupChannelId: selected.id,
      });
      setSelectedChannelId("");
      toast.success("Канал привязан");
      await invalidateChannelData();
    } catch (error) {
      showOperationError(error, errorMessage(error), setChannelError);
    } finally {
      setChannelPending(false);
    }
  }

  async function handleUnbindChannel(channelId: string) {
    if (channelPending) return;
    setChannelPending(true);
    setChannelError(null);
    try {
      await adminApi.unbindChannel(token!, funnelData.id, channelId);
      toast.success("Канал отвязан");
      await invalidateChannelData();
    } catch (error) {
      showOperationError(error, errorMessage(error), setChannelError);
    } finally {
      setChannelPending(false);
    }
  }

  async function handleTestSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (testPending) return;
    const message = testMessage.trim();
    if (!message) {
      setTestError("Введите сообщение для теста");
      return;
    }
    setTestPending(true);
    setTestError(null);
    try {
      const result = await adminApi.testFunnel(token!, funnelData.id, {
        message,
      });
      const userTurn: TestTurn = {
        id: nextTurnIdRef.current++,
        role: "USER",
        content: message,
      };
      const assistantTurn: TestTurn = {
        id: nextTurnIdRef.current++,
        role: "ASSISTANT",
        content: result.reply,
        ...(result.proposedActions
          ? { proposedActions: result.proposedActions }
          : {}),
      };
      setTestTurns((current) => [...current, userTurn, assistantTurn]);
      setTestMessage("");
    } catch (error) {
      setTestError(errorMessage(error));
    } finally {
      setTestPending(false);
    }
  }

  async function handleDelete() {
    if (deletePending || deleteConflict) return;
    setDeletePending(true);
    setDeleteError(null);
    try {
      await adminApi.deleteFunnel(token!, funnelData.id, {
        expectedUpdatedAt,
        confirmationName: persistedName,
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["admin", "account", funnelData.accountId],
        }),
        queryClient.invalidateQueries({ queryKey: ["admin", "accounts"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "funnels"] }),
        queryClient.invalidateQueries({
          queryKey: ["admin", "funnel", funnelData.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ["admin", "account", funnelData.accountId, "funnels"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["admin", "account", funnelData.accountId, "audit"],
        }),
      ]);
      toast.success("Агент удалён");
      navigate(`/accounts/${funnelData.accountId}/agents`);
    } catch (error) {
      setDeleteConflict(error instanceof ApiError && error.status === 409);
      setDeleteError(errorMessage(error));
    } finally {
      setDeletePending(false);
    }
  }

  const pipelineOptions = pipelines.data?.some(
    (pipeline) => pipeline.id === funnelData.amoPipelineId,
  )
    ? pipelines.data
    : [
        {
          id: funnelData.amoPipelineId,
          name: funnelData.amoPipelineId,
          bound: true,
        },
        ...(pipelines.data ?? []),
      ];

  return (
    <div>
      <PageHeader
        title={funnelData.name}
        description={`Аккаунт ${funnelData.accountId}`}
        backTo={`/accounts/${funnelData.accountId}/agents`}
        backLabel="К агентам клиента"
      />
      <FunnelTabs active={activeTab} funnelId={funnelData.id} />

      {activeTab === "settings" ? (
        <div className="space-y-4">
          <form
            className="panel p-4"
            noValidate
            onSubmit={handleSettingsSubmit}
          >
            <h2 className="text-lg font-semibold">Настройки агента</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium">
                <span className="mb-1.5 block">Воронка amoCRM</span>
                <select
                  className="control"
                  value={funnelData.amoPipelineId}
                  disabled
                >
                  {pipelineOptions.map((pipeline) => (
                    <option key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium">
                <span className="mb-1.5 block">Название агента</span>
                <input
                  className="control"
                  value={name}
                  disabled={settingsPending}
                  onChange={(event) => {
                    setName(event.target.value);
                  }}
                />
              </label>
              <fieldset
                className="sm:col-span-2"
                disabled={settingsPending || statuses.isLoading}
              >
                <legend className="text-sm font-medium">
                  Разрешённые этапы
                </legend>
                <p className="mt-1 text-xs text-muted-foreground">
                  Пустой выбор разрешает агенту отвечать на всех этапах.
                </p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {(statuses.data ?? []).map((status) => (
                    <label
                      key={status.id}
                      className="flex items-center gap-2 rounded-md border p-3 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={statusIds.includes(status.id)}
                        onChange={() => toggleStatus(status.id)}
                      />
                      <span>{status.name}</span>
                    </label>
                  ))}
                  {missingStatusIds.map((statusId) => (
                    <label
                      key={statusId}
                      className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm"
                    >
                      <input
                        type="checkbox"
                        aria-label={`Сохранённый недоступный этап ${statusId}`}
                        checked={statusIds.includes(statusId)}
                        onChange={() => toggleStatus(statusId)}
                      />
                      <span>
                        <span className="block font-medium text-warning-foreground">
                          Этап больше недоступен в amoCRM
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          ID: {statusId}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                {statuses.error ? (
                  <p className="mt-2 text-sm text-danger" role="alert">
                    {statuses.error.message}
                  </p>
                ) : null}
              </fieldset>
              <div className="sm:col-span-2">
                <AdminPromptEditor
                  token={token!}
                  accountId={funnelData.accountId}
                  value={systemPrompt}
                  onChange={setSystemPrompt}
                  fields={promptFields}
                  onFieldsChange={setPromptFields}
                  disabled={settingsPending}
                />
              </div>
              <label className="text-sm font-medium">
                <span className="mb-1.5 block">Модель</span>
                <select
                  className="control"
                  value={model}
                  disabled={settingsPending}
                  onChange={(event) => {
                    if (isLlmModel(event.target.value)) {
                      setModel(event.target.value);
                    }
                  }}
                >
                  {models.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-medium">
                <span className="mb-1.5 block">Стоп-фраза</span>
                <input
                  className="control"
                  value={stopPhrase}
                  disabled={settingsPending}
                  placeholder="Необязательно"
                  onChange={(event) => {
                    setStopPhrase(event.target.value);
                  }}
                />
              </label>
              <CrmUpdateSettings
                allowLeadUpdates={allowLeadUpdates}
                allowContactUpdates={allowContactUpdates}
                onLead={setAllowLeadUpdates}
                onContact={setAllowContactUpdates}
                loadCatalog={(entity) =>
                  adminApi.crmFields(token!, funnel.data!.accountId, entity)
                }
                queryKey={`admin:${funnel.data!.accountId}`}
                disabled={settingsPending}
              />
              <FollowupSettings
                channels={funnelData.channels}
                loadTemplates={(channelId) =>
                  adminApi.wabaTemplates(token!, funnelData.id, channelId)
                }
                preview={(input, signal) =>
                  adminApi.previewFollowup(
                    token!,
                    funnelData.id,
                    { ...input, expectedUpdatedAt: funnel.data!.updatedAt },
                    signal,
                  )
                }
                queryKey={`admin:${funnelData.id}`}
                load={() => adminApi.getFollowups(token!, funnelData.id)}
                reloadDependencies={async () => {
                  const refreshed = await adminApi.funnel(
                    token!,
                    funnelData.id,
                  );
                  queryClient.setQueryData(
                    ["admin", "funnel", funnelData.id],
                    refreshed,
                  );
                }}
                save={(input) =>
                  adminApi.saveFollowups(token!, funnelData.id, {
                    ...input,
                    expectedUpdatedAt: funnel.data!.updatedAt,
                  })
                }
                onSaved={() => {
                  void invalidateFunnelData(
                    funnelData.accountId,
                    funnelData.id,
                  );
                }}
                hasChannels={Boolean(funnelData.channels.length)}
              />
              <OutcomeSettings
                enabled={escalateOnGoal}
                target={goalStatusId}
                working={statusIds}
                stages={statuses.data ?? []}
                criteriaStatus={funnel.data?.outcomeCriteriaStatus ?? "PENDING"}
                disabled={settingsPending}
                loading={statuses.isPending}
                error={statuses.isError}
                onRetry={() => {
                  void statuses.refetch();
                }}
                onEnabled={setEscalateOnGoal}
                onTarget={setGoalStatusId}
              />
              <EscalationSettings
                enabled={escalationEnabled}
                target={escalationStatusId}
                working={statusIds}
                stages={statuses.data ?? []}
                onEnabled={setEscalationEnabled}
                onTarget={setEscalationStatusId}
                disabled={settingsPending}
              />
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={active}
                  disabled={settingsPending}
                  onChange={(event) => {
                    setActive(event.target.checked);
                  }}
                />
                Агент активен
              </label>
            </div>
            <div className="mt-4 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-end">
              {settingsError ? (
                <p className="text-sm text-danger sm:mr-auto" role="alert">
                  {settingsError}
                  <button
                    type="button"
                    className="ml-2 underline"
                    onClick={async () => {
                      const latest = await funnel.refetch();
                      if (latest.data) {
                        setExpectedUpdatedAt(latest.data.updatedAt);
                        setSettingsError(
                          "Актуальная версия загружена. Черновик сохранён; проверьте его перед повторным сохранением.",
                        );
                      }
                    }}
                  >
                    Обновить актуальное состояние
                  </button>
                </p>
              ) : null}
              <button
                className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
                type="submit"
                aria-busy={settingsPending}
                disabled={settingsPending}
              >
                {settingsPending ? "Сохраняем..." : "Сохранить настройки"}
              </button>
            </div>
          </form>

          <section className="panel border-danger/30 p-4">
            <h2 className="text-lg font-semibold text-danger">
              Удаление агента
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Безвозвратно удалятся {funnelData.channels.length} канал(а),{" "}
              {funnelData.dialogsCount} диалог(а), связанные сообщения и{" "}
              {funnelData.runsCount} запуск(а).
            </p>
            <button
              className="mt-4 inline-flex h-10 items-center justify-center rounded-md border border-danger/30 px-4 text-sm font-medium text-danger disabled:opacity-50"
              type="button"
              disabled={deletePending}
              onClick={() => setDeleteOpen(true)}
            >
              Удалить агента
            </button>
            {deleteError ? (
              <p className="mt-3 text-sm text-danger" role="alert">
                {deleteError}
              </p>
            ) : null}
          </section>
        </div>
      ) : null}

      {activeTab === "channels" ? (
        <section className="panel p-4">
          <h2 className="text-lg font-semibold">Каналы Wazzup</h2>
          <div className="mt-4 space-y-2">
            {funnelData.channels.map((channel) => {
              const publicChannel = wazzupChannels.data?.find(
                (item) => item.id === channel.wazzupChannelId,
              );
              const channelName =
                publicChannel?.name ?? channel.wazzupChannelId;
              return (
                <div
                  key={channel.id}
                  className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="text-sm font-medium">{channelName}</p>
                    <p className="text-xs text-muted-foreground">
                      {channel.type}
                    </p>
                  </div>
                  <button
                    className="inline-flex h-9 items-center justify-center rounded-md border px-3 text-sm font-medium disabled:opacity-50"
                    type="button"
                    aria-label={`Отключить ${channelName}`}
                    disabled={channelPending}
                    onClick={() => handleUnbindChannel(channel.id)}
                  >
                    Отключить
                  </button>
                </div>
              );
            })}
            {funnelData.channels.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Каналы не подключены.
              </p>
            ) : null}
          </div>

          <form className="mt-5 border-t pt-4" onSubmit={handleBindChannel}>
            <label className="block text-sm font-medium">
              <span className="mb-1.5 block">Новый Wazzup-канал</span>
              <select
                className="control"
                value={selectedChannelId}
                disabled={channelPending || wazzupChannels.isLoading}
                onChange={(event) => setSelectedChannelId(event.target.value)}
              >
                <option value="">Выберите канал</option>
                {(wazzupChannels.data ?? []).map((channel) => (
                  <option
                    key={channel.id}
                    value={channel.id}
                    disabled={funnelData.channels.some(
                      (current) => current.wazzupChannelId === channel.id,
                    )}
                  >
                    {channel.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="mt-3 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
              type="submit"
              aria-busy={channelPending}
              disabled={channelPending || !selectedChannelId}
            >
              {channelPending ? "Подключаем..." : "Подключить канал"}
            </button>
            {wazzupChannels.error ? (
              <p className="mt-3 text-sm text-danger" role="alert">
                {wazzupChannels.error.message}
              </p>
            ) : null}
            {channelError ? (
              <p className="mt-3 text-sm text-danger" role="alert">
                {channelError}
              </p>
            ) : null}
          </form>
        </section>
      ) : null}

      {activeTab === "test" ? (
        <section className="panel p-4">
          <h2 className="text-lg font-semibold">Тест агента</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Тест не создаёт диалог, не сохраняет сообщения и не отправляет их в
            Wazzup.
          </p>
          <div className="mt-4 space-y-2" aria-live="polite">
            {testTurns.map((turn) => (
              <Fragment key={turn.id}>
              {turn.content && <div
                data-client-message
                className={`rounded-md border p-3 text-sm ${
                  turn.role === "USER" ? "ml-6 bg-accent/40" : "mr-6 bg-muted"
                }`}
              >
                <p className="mb-1 text-xs font-medium text-muted-foreground">
                  {turn.role === "USER" ? "Администратор" : "Агент"}
                </p>
                <div className="whitespace-pre-wrap">
                  {turn.content}
                </div>
              </div>}
              {turn.proposedActions?.map((action, index) => (
                <div key={index} role="status" aria-label="Результат симуляции" className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
                  <p className="text-xs font-medium text-muted-foreground">Результат симуляции · клиенту не отправляется</p>
                  <p className="mt-1 font-medium">Предложена передача менеджеру на этап {action.targetStatusId}</p>
                  <p>{action.reason}</p>
                  <p className="mt-1 text-xs text-muted-foreground">Сделка в CRM не изменена.{!turn.content ? " Ответ клиенту не предусмотрен." : ""}</p>
                </div>
              ))}
              </Fragment>
            ))}
          </div>
          <p className="mt-3 text-sm">
            Тест использует сохранённого агента. Сохраните правки перед
            проверкой новой инструкции.
          </p>
          <form className="mt-4" onSubmit={handleTestSubmit}>
            <label className="block text-sm font-medium">
              <span className="mb-1.5 block">Сообщение агенту</span>
              <textarea
                className="control min-h-24"
                value={testMessage}
                disabled={testPending}
                onChange={(event) => setTestMessage(event.target.value)}
              />
            </label>
            <button
              className="mt-3 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
              type="submit"
              aria-busy={testPending}
              disabled={testPending}
            >
              {testPending ? "Ждём ответ..." : "Отправить тест"}
            </button>
            {testError ? (
              <p className="mt-3 text-sm text-danger" role="alert">
                {testError}
              </p>
            ) : null}
          </form>
        </section>
      ) : null}

      {activeTab === "runs" ? (
        <RunsTab key={funnelData.id} funnelId={funnelData.id} />
      ) : null}

      {activeTab === "dialogs" ? (
        <DialogsTab
          key={funnelData.id}
          accountId={funnelData.accountId}
          funnelId={funnelData.id}
          channels={funnelData.channels}
        />
      ) : null}

      {deleteOpen ? (
        <ConfirmDialog
          title="Удалить агента без восстановления"
          description={`Будут удалены ${funnelData.channels.length} канал(а), ${funnelData.dialogsCount} диалог(а), сообщения и ${funnelData.runsCount} запуск(а).${deleteError ? ` ${deleteError}` : ""}`}
          confirmationText={persistedName}
          confirmLabel="Удалить без восстановления"
          destructive
          confirmDisabled={deleteConflict}
          isPending={deletePending}
          onConfirm={handleDelete}
          onCancel={() => setDeleteOpen(false)}
        />
      ) : null}
    </div>
  );
}
